import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import StaticLeasesSection from './dhcp-server/static-leases-section';
import SubnetsSection from './dhcp-server/subnets-section';
import {
  type DhcpSubnet,
  type VidSetting,
  type StaticLease,
  type BridgeRuntime,
} from './dhcp-server/types';

export default function WebUiSettingsDhcpServerPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [vids, setVids] = React.useState<VidSetting[]>([]);
  const [subnets, setSubnets] = React.useState<DhcpSubnet[]>([]);
  const [leases, setLeases] = React.useState<StaticLease[]>([]);
  const [bridges, setBridges] = React.useState<BridgeRuntime[]>([]);
  const [newSubnet, setNewSubnet] = React.useState<DhcpSubnet>({
    ip: '',
    mask: '',
    leasetime: 86400,
    pool_start: '0.0.0.0',
    pool_end: '0.0.0.0',
    options: [],
  });
  const [newLease, setNewLease] = React.useState({
    ip: '',
    type: 'Port based' as 'Port based' | 'MAC',
    vlanId: 0,
    port: 0,
    mac: '',
    deploymentArchive: `${window.location.origin}/files/files.tar`,
    deploymentFilename: '',
    addVendorOption: false,
  });
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [vidData, subnetData, leaseData, bridgeData] = await Promise.all([
      postWebUiApi<any[]>({
        service: 'dhcp',
        access: 'get',
        command: 'get vids',
      }),
      postWebUiApi<DhcpSubnet[]>({
        service: 'dhcp',
        access: 'get',
        command: 'get subnet',
      }),
      postWebUiApi<StaticLease[]>({
        service: 'dhcp',
        access: 'get',
        command: 'get cfg leases',
      }),
      postWebUiApi<BridgeRuntime[]>({
        service: 'vlan',
        access: 'get',
        command: 'get current bridge config',
      }),
    ]);
    const normalizedVids = (vidData ?? []).map((v: any) =>
      typeof v === 'number'
        ? { vid: v, type: 'server' as const }
        : {
            vid: Number(v.vid ?? 0),
            type: (v.type ?? 'server') as VidSetting['type'],
          },
    );
    setVids(normalizedVids);
    setSubnets(subnetData ?? []);
    setLeases(leaseData ?? []);
    setBridges(bridgeData ?? []);
    if (normalizedVids.length) {
      setNewLease((p) => ({ ...p, vlanId: normalizedVids[0].vid }));
    }
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const run = React.useCallback(
    async (fn: () => Promise<void>, ok: string) => {
      try {
        await fn();
        setMessage(ok);
        setError(null);
        await reload();
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [reload],
  );

  return (
    <WebUiPageWrapper titleList={['Settings', 'DHCP Server']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <h4 className="font-bold">DHCP per Interface</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Interface</th>
                <th>Status DHCP</th>
              </tr>
            </thead>
            <tbody>
              {vids.map((v) => (
                <tr key={v.vid} className="webui-table-row-content">
                  <td>{v.vid === 0 ? 'Lan' : `VLAN_ID${v.vid}`}</td>
                  <td>
                    <WebUiSelect
                      className="w-full"
                      inputSize="sm"
                      options={[
                        { label: 'Server', value: 'server' },
                        { label: 'Relay', value: 'relay' },
                        { label: 'Inserter', value: 'inserter' },
                        { label: 'Off', value: 'off' },
                      ]}
                      disabled={readonly}
                      value={v.type}
                      onChange={(e) =>
                        setVids((prev) =>
                          prev.map((x) =>
                            x.vid === v.vid
                              ? {
                                  ...x,
                                  type: e.target.value as VidSetting['type'],
                                }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={() =>
                run(
                  () =>
                    postWebUiApi({
                      service: 'dhcp',
                      access: 'set',
                      command: 'set vids',
                      parameters: vids,
                    }),
                  'DHCP interface settings updated.',
                )
              }
            >
              Set
            </WebUiButton>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <SubnetsSection
            readonly={readonly}
            subnets={subnets}
            setSubnets={setSubnets}
            onUpdateSubnet={(subnet) =>
              run(
                () =>
                  postWebUiApi({
                    service: 'dhcp',
                    access: 'set',
                    command: 'edit subnet',
                    parameters: subnet,
                  }),
                'Subnet updated.',
              )
            }
            onRemoveSubnet={(subnet) =>
              run(
                () =>
                  postWebUiApi({
                    service: 'dhcp',
                    access: 'set',
                    command: 'remove subnet',
                    parameters: { ip: subnet.ip, mask: subnet.mask },
                  }),
                'Subnet removed.',
              )
            }
          />
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <h4 className="font-bold">Add IP Network to DHCP Server</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Interface</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    options={bridges.map((b) => ({
                      label: b.vlan_id === 0 ? 'Lan' : `VLAN_ID${b.vlan_id}`,
                      value: `${b.vlan_id}|${b.ip ?? ''}|${b.sm ?? ''}`,
                    }))}
                    disabled={readonly}
                    onChange={(e) => {
                      const [vid, ip, sm] = e.target.value.split('|');
                      setNewSubnet((p) => ({ ...p, ip, mask: sm }));
                      setNewLease((p) => ({ ...p, vlanId: Number(vid) }));
                    }}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Network Address</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly}
                    value={newSubnet.ip}
                    onChange={(e) =>
                      setNewSubnet((p) => ({ ...p, ip: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Network Mask</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly}
                    value={newSubnet.mask}
                    onChange={(e) =>
                      setNewSubnet((p) => ({ ...p, mask: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Leasetime (s)</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="number"
                    disabled={readonly}
                    value={newSubnet.leasetime}
                    onChange={(e) =>
                      setNewSubnet((p) => ({
                        ...p,
                        leasetime: Number(e.target.value),
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Pool</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly}
                    value={newSubnet.pool_start}
                    onChange={(e) =>
                      setNewSubnet((p) => ({
                        ...p,
                        pool_start: e.target.value,
                      }))
                    }
                  />{' '}
                  -{' '}
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly}
                    value={newSubnet.pool_end}
                    onChange={(e) =>
                      setNewSubnet((p) => ({ ...p, pool_end: e.target.value }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={() =>
                run(
                  () =>
                    postWebUiApi({
                      service: 'dhcp',
                      access: 'set',
                      command: 'set subnet',
                      parameters: newSubnet,
                    }),
                  'IP network added.',
                )
              }
            >
              Add IP Network
            </WebUiButton>
          </div>
        </div>
        <StaticLeasesSection
          readonly={readonly}
          leases={leases}
          newLease={newLease}
          setNewLease={setNewLease}
          vlanOptions={vids.map((v) => v.vid)}
          portOptions={Array.from({ length: 24 }, (_, idx) => `P${idx + 1}`)}
          onAddLease={() =>
            run(
              () =>
                postWebUiApi({
                  service: 'dhcp',
                  access: 'set',
                  command: 'add cfg leases',
                  parameters: {
                    ip: newLease.ip,
                    type: newLease.type === 'MAC' ? 1 : 0,
                    value:
                      newLease.type === 'MAC'
                        ? [newLease.mac]
                        : [newLease.vlanId, newLease.port],
                    options: newLease.addVendorOption
                      ? [
                          {
                            code: 125,
                            type: 4,
                            value: [
                              newLease.deploymentArchive,
                              newLease.deploymentFilename,
                            ],
                          },
                        ]
                      : [],
                  },
                }),
              'Static lease added.',
            )
          }
          onRemoveLease={(ip) =>
            run(
              () =>
                postWebUiApi({
                  service: 'dhcp',
                  access: 'set',
                  command: 'remove cfg leases',
                  parameters: { ip },
                }),
              'Static lease removed.',
            )
          }
        />
      </div>
    </WebUiPageWrapper>
  );
}
